import React, { useState } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { IconInteractiveViewer, IconNavigationArrowRight, IconSquareArrow } from '../Icons';
import { Link } from '../Link/Link';
import { OverlayProvider } from 'react-aria';
import { Modal } from '../Modal/Modal';
import { altiumViewerClient } from '../../utils/altium-script';

import './ProductHeader.scss';
import { TooltipTrigger } from '../TooltipTrigger/TooltipTrigger';
import { INTERACTIVE } from '../../utils/constants';
import { MDXRenderer } from 'gatsby-plugin-mdx';

interface ProductHeaderProps {
  image: IGatsbyImageData | any;
  interactive: {
    edges: Array<any>;
  };
  product: {
    body: string;
    frontmatter: {
      title: string;
      url_guide: string;
      url_datasheet: string;
      status: string;
      primary_button_url?: string;
      primary_button_title?: string;
      secondary_button_url?: string;
      secondary_button_title?: string;
    };
  };
  purchaseLink: string;
}

// ProductHeader expects an image file and a product mdx
export default function ProductHeader({ image, product, interactive, purchaseLink }: ProductHeaderProps) {
  const [openModal, setOpenModal] = useState(false);

  //find board view
  let interactiveBoardFileSource = interactive.edges.find((element) => element.node.base.endsWith(INTERACTIVE[0].file));
  let interactiveBoardFileEncrypted = interactive.edges.find((element) => element.node.base.endsWith(INTERACTIVE[1].file));
  let interactiveBoardFile = interactiveBoardFileSource ?? interactiveBoardFileEncrypted;

  const altiumModal = (altiumFile) => (
    <OverlayProvider>
      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        isDismissable={true}
        title={'Interactive viewer'}
        className="altium-modal"
        mobileFull={true}
        full={true}
      >
        {/* it's the div that the altium script parse to generate the iframe */}
        <div
          id="viewer-frame_container"
          ref={() => {
            if (window) {
              altiumViewerClient.getViewer().initViewer();
            }
          }}
          className="altium-ecad-viewer"
          style={{ height: '500px' }}
          //data-project-src={window && window.location.origin + altiumFile}
          data-project-src={window && 'https://docs.arduino.cc' + altiumFile}
          data-enabled-views="3d,pcb,sch"
          data-hide-project-src
          data-project-outputs="None"
        ></div>
      </Modal>
    </OverlayProvider>
  );

  const openAltiumModal = (event) => {
    event.preventDefault();
    setOpenModal(true);
  };

  return (
    <section className="product-header">
      <div className="product-header__text">
        <div className="product-header__title">
          <div className="product-header__title--text">
            {product.frontmatter.title && <h1>{product.frontmatter.title}</h1>}
            {product.frontmatter.status === 'end-of-life' && <div className="product-header__title--status">End&nbsp;of&nbsp;Life</div>}
            {product.frontmatter.status === 'retired' && <div className="product-header__title--status">Retired</div>}
          </div>
        </div>
        <div className="product-header__description">
          <div className="product-header__content">
            <MDXRenderer>{product.body}</MDXRenderer>
          </div>
          <div className="product-header__buttons">
            {product.frontmatter.primary_button_url && (
              <Link
                size="small"
                variant="primary"
                href={product.frontmatter.primary_button_url}
                onClick={() => window.data?.dataLayer.push({ event: 'getting-started' })}
              >
                <span>{product.frontmatter.primary_button_title}</span>
              </Link>
            )}
            {product.frontmatter.secondary_button_url && (
              <Link
                size="small"
                variant="tertiary"
                href={product.frontmatter.secondary_button_url}
                onClick={() => window.data?.dataLayer.push({ event: 'installation' })}
              >
                <span className="product-header__buttons--space-right">{product.frontmatter.secondary_button_title}</span>
                <IconNavigationArrowRight />
              </Link>
            )}
          </div>
        </div>
      </div>
      <div className="product-header__image">
        {interactiveBoardFile && (
          <>
            <TooltipTrigger
              TriggerElement={
                <button className="product-header__title--viewer" onClick={openAltiumModal}>
                  <IconInteractiveViewer /> <span>Interactive Viewer</span>
                </button>
              }
              delay={0}
              tooltipPlacement="top"
              tooltipStrategy="absolute"
              tooltipType="tooltip"
              onClick={openAltiumModal}
            >
              Open Altium Interactive viewer
            </TooltipTrigger>
            {openModal && altiumModal(interactiveBoardFile.node.publicURL)}
          </>
        )}
        {image && image.extension === 'svg' ? (
          // svg support
          <img src={image.publicURL} alt={`${product.frontmatter.title}-board`} />
        ) : (
          <GatsbyImage
            alt={`${product.frontmatter.title}-board`}
            objectFit="contain"
            style={{ height: '100%' }}
            imgStyle={{ objectFit: 'contain' }}
            image={image?.childImageSharp?.gatsbyImageData}
          />
        )}
        {product.frontmatter.status !== 'end-of-life' && product.frontmatter.status !== 'retired' && (
          <a href={purchaseLink} className="product-header__purchase" target="_blank" rel="noreferrer">
            <span>Buy now</span>
            <IconSquareArrow />
          </a>
        )}
      </div>
    </section>
  );
}
